// QuikQ Product Code to Fuel Type Name mapping
// Based on QuikQ API documentation
export const FUEL_TYPE_NAMES: Record<string, string> = {
  '1': 'Unleaded Regular',
  '2': 'Unleaded Plus',
  '3': 'Unleaded Super',
  '4': 'Unleaded 4',
  '5': 'Unleaded 5',
  '6': 'Unleaded Methanol (5.7%)',
  '7': 'Unleaded Plus Methanol (5.7%)',
  '8': 'Super Unleaded Methanol (5.7%)',
  '9': 'Unleaded Methanol (7.7%)',
  '10': 'Unleaded Plus Methanol (7.7%)',
  '11': 'Unleaded Ethanol (5.7%)',
  '12': 'Unleaded Plus Ethanol (5.7%)',
  '13': 'Super Unleaded Ethanol (5.7%)',
  '14': 'Unleaded Ethanol (7.7%)',
  '15': 'Unleaded Plus Ethanol (7.7%)',
  '16': 'Methanol/Leaded',
  '17': 'Ethanol/Leaded',
  '18': 'Leaded',
  '19': 'Regular Diesel #2',
  '20': 'Premium Diesel #2',
  '21': 'Diesel #1',
  '22': 'Compressed Natural Gas',
  '23': 'Liquid Propane Gas',
  '24': 'Liquid Natural Gas',
  '25': 'M-85',
  '26': 'E-85',
  '27': 'Unleaded Reformulated 1',
  '28': 'Unleaded Reformulated 2',
  '29': 'Unleaded Reformulated 3',
  '30': 'Unleaded Reformulated 4',
  '31': 'Unleaded Reformulated 5',
  '33': 'Bio Diesel',
  '36': 'Racing Fuel',
  '37': 'Super Unleaded Methanol (7.7%)',
  '38': 'Unleaded Methanol (10%)',
  '39': 'Unleaded Plus Methanol (10%)',
  '40': 'Super Unleaded Methanol (10%)',
  '46': 'DEF (Diesel Exhaust Fluid)',
  '50': 'Bio Diesel B20',
  '51': 'Reefer Fuel',
  '52': 'Off-Road Diesel',
  '53': 'Premium Unleaded',
  '54': 'Mid-Grade Unleaded'
};

export function getFuelTypeName(prodCode: string | number): string {
  const code = String(prodCode || '').trim();
  return FUEL_TYPE_NAMES[code] || code || 'Fuel';
}

/**
 * Primary label shown next to a price in the UI.
 * QuikQ prod code 19 is shown as the Northstar discounted line.
 */
export function getFuelPriceDisplayLabel(prodCode: string | number): string {
  const code = String(prodCode || '').trim();
  if (code === '19') return 'Northstar price';
  return getFuelTypeName(prodCode);
}

/** Northstar discounted diesel row (prod 19) */
export function isNorthstarOrRetailPriceRow(prodCode: string | number): boolean {
  const code = String(prodCode || '').trim();
  return code === '19';
}

/**
 * Decides whether a QuikQ repricing row should surface in the UI.
 *
 * Product policy (per product owner): we only ever show TWO prices per
 * station — "Northstar price" and "Retail price", both of which live on
 * the SAME prod-19 row (`adjPrice` = Northstar, `retailPrice` = Retail).
 * Every other fuel type returned by QuikQ (Bio Diesel = 33, DEF = 46,
 * unleaded variants, racing fuel, propane, off-road diesel, etc.) is
 * intentionally hidden — drivers in our user base only care about
 * highway diesel.
 *
 * If a station ever needs to display additional fuel types, this is the
 * one place to relax the filter.
 */
export function shouldShowRepricingRow(
  prodCode: string | number,
  price: number | null | undefined
): boolean {
  const code = String(prodCode || '').trim();
  if (code !== '19') return false;
  if (price == null) return false;
  const n = Number(price);
  if (!Number.isFinite(n)) return false;
  return true;
}

/**
 * Pick the price value that should be displayed for a repricing row.
 * - prod 19 (Northstar): client/adjusted price when present, else retail
 * - prod 20 (Retail): prefer retail/list price
 * - other products: prefer adjusted price when available, fallback to retail
 */
export function getRepricingDisplayPrice(
  prodCode: string | number,
  adjPrice: number | null | undefined,
  retailPrice: number | null | undefined
): number | null {
  const code = String(prodCode || '').trim();
  const adj =
    typeof adjPrice === 'number' && Number.isFinite(adjPrice) && adjPrice > 0 ? adjPrice : null;
  const retail =
    typeof retailPrice === 'number' && Number.isFinite(retailPrice) && retailPrice > 0
      ? retailPrice
      : null;

  if (code === '19') return adj ?? retail;
  if (code === '20') return retail ?? adj;
  return adj ?? retail;
}

/** Northstar line for prod 19 — same rules as {@link getRepricingDisplayPrice}. */
export function getNorthstarDisplayPrice(
  prodCode: string | number,
  adjPrice: number | null | undefined,
  retailPrice: number | null | undefined
): number | null {
  return getRepricingDisplayPrice(prodCode, adjPrice, retailPrice);
}

/**
 * Northstar discount station: at least one repricing line with adjPrice > 0.
 * Used for "Suggested nearby" and for showing only these stations on the map.
 */
export function stationQualifiesForSuggestedNearby(station: {
  repricing?: Array<{ adjPrice?: number; retailPrice?: number; prodCode?: string | number }>;
}): boolean {
  const list = Array.isArray(station.repricing) ? station.repricing : [];
  for (const r of list) {
    const price = getRepricingDisplayPrice(r.prodCode ?? '19', r.adjPrice, r.retailPrice);
    if (price != null && price > 0) return true;
  }
  return false;
}

/**
 * Check for price override and return appropriate price for display.
 * If an override exists for this station+product, use the override price.
 * Otherwise, fall back to the standard display price logic.
 *
 * @param stationCode - The station code to check for overrides
 * @param prodCode - The product code
 * @param adjPrice - The adjusted/discounted price from QuikQ
 * @param retailPrice - The retail/list price from QuikQ
 * @param overridesMap - Map of overrides keyed by "stationCode:prodCode"
 * @returns The price to display (override if exists, else standard logic)
 */
export function getDisplayPriceWithOverride(
  stationCode: string | number,
  prodCode: string | number,
  adjPrice: number | null | undefined,
  retailPrice: number | null | undefined,
  overridesMap?: Record<string, any>
): number | null {
  // Check for override if map is provided
  if (overridesMap && stationCode && prodCode) {
    const key = `${stationCode}:${prodCode}`;
    const override = overridesMap[key];
    if (override && typeof override.overridePrice === 'number') {
      return override.overridePrice;
    }
  }

  // Fall back to standard display price logic
  return getRepricingDisplayPrice(prodCode, adjPrice, retailPrice);
}

/**
 * Retail/list price for the prod-19 row — prefers `retailPrice` over `adjPrice`.
 */
export function getRetailDisplayPrice(
  prodCode: string | number,
  adjPrice: number | null | undefined,
  retailPrice: number | null | undefined
): number | null {
  const code = String(prodCode || '').trim();
  if (code !== '19') return null;
  const retail =
    typeof retailPrice === 'number' && Number.isFinite(retailPrice) && retailPrice > 0
      ? retailPrice
      : null;
  if (retail != null) return retail;
  const adj =
    typeof adjPrice === 'number' && Number.isFinite(adjPrice) && adjPrice > 0 ? adjPrice : null;
  return adj;
}

/** Score how well a station name matches a search query (higher = better). */
export function scoreStationSearchMatch(
  station: {
    stationName?: string | null;
    city?: string | null;
    state?: string | null;
    address?: string | null;
  },
  query: string
): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = (station.stationName || '').trim().toLowerCase();
  if (!name) return 0;
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  if (name.includes(q)) return 60;
  const tokens = q.split(/\s+/).filter((t) => t.length >= 2);
  if (tokens.length > 1 && tokens.every((t) => name.includes(t))) return 50;
  return 0;
}

/**
 * Rank station search hits; when there is an exact name match, return only those.
 */
export function filterStationSearchResults<
  T extends {
    stationName?: string | null;
    city?: string | null;
    state?: string | null;
    address?: string | null;
  },
>(stations: T[], query: string, limit = 8): T[] {
  const scored = stations
    .map((s) => ({ s, score: scoreStationSearchMatch(s, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.some((x) => x.score === 100)) {
    return scored.filter((x) => x.score === 100).slice(0, limit).map((x) => x.s);
  }
  if (scored.some((x) => x.score >= 80)) {
    return scored.filter((x) => x.score >= 80).slice(0, limit).map((x) => x.s);
  }
  return scored.slice(0, limit).map((x) => x.s);
}

function resolveNetworkSlug(station: {
  network?: string | null;
  stationCode?: string | null;
  stationName?: string | null;
  affiliates?: Array<{ afType?: string | null; afCode?: string | null; afName?: string | null }> | null;
}): string | null {
  const affiliates = Array.isArray(station.affiliates) ? station.affiliates : [];
  for (const a of affiliates) {
    if (String(a?.afType || '').toUpperCase() !== 'BRAND') continue;
    const code = (a?.afCode ?? '').toLowerCase();
    if (code) return code;
    const afName = (a?.afName ?? '').toLowerCase();
    if (afName.includes('caseys') || afName.includes("casey's")) return 'caseys';
    if (afName.includes('ambest') || afName.includes('am best')) return 'ambest';
  }

  const network = (station.network ?? '').trim().toLowerCase();
  if (network) return network;

  const code = String(station.stationCode ?? '').trim();
  const colonIdx = code.indexOf(':');
  if (colonIdx > 0) return code.slice(0, colonIdx).toLowerCase();

  const name = (station.stationName ?? '').toLowerCase();
  if (name.includes("casey's") || name.includes('caseys')) return 'caseys';
  if (name.includes('ambest') || name.includes('am best')) return 'ambest';

  return null;
}

/** Casey's and AM Best always show separate Northstar + Retail lines on station cards. */
export function isDualPriceNetworkStation(station: {
  network?: string | null;
  stationCode?: string | null;
  stationName?: string | null;
  affiliates?: Array<{ afType?: string | null; afCode?: string | null; afName?: string | null }> | null;
}): boolean {
  const slug = resolveNetworkSlug(station);
  return slug === 'caseys' || slug === 'ambest';
}

/**
 * Prices for the two-line station card (Northstar + Retail).
 * TEMPORARY: AMBEST & Casey's show retail on both lines until client pricing is finalized.
 */
export function getStationCardPrices(
  station: {
    network?: string | null;
    stationCode?: string | null;
    stationName?: string | null;
    affiliates?: Array<{ afType?: string | null; afCode?: string | null; afName?: string | null }> | null;
  },
  stationCode: string | number,
  prodCode: string | number,
  adjPrice: number | null | undefined,
  retailPrice: number | null | undefined,
  overridesMap?: Record<string, any>
): { northstar: number | null; retail: number | null } {
  const retail = getRetailDisplayPrice(prodCode, adjPrice, retailPrice);
  if (isDualPriceNetworkStation(station)) {
    const price =
      retail ?? getDisplayPriceWithOverride(stationCode, prodCode, adjPrice, retailPrice, overridesMap);
    return { northstar: price, retail: price };
  }
  const northstar = getDisplayPriceWithOverride(stationCode, prodCode, adjPrice, retailPrice, overridesMap);
  return { northstar, retail: retail ?? northstar };
}

const FUEL_NETWORK_LABELS: Record<string, string> = {
  caseys: "Casey's",
  tapetro: 'TA / Petro',
  racetrac: 'RaceTrac',
  sappbros: 'Sapp Bros',
  ambest: 'AMBEST',
  maverik: 'Maverik',
  petro: 'Petro',
  northstar: 'Northstar',
};

/**
 * Human-readable fuel network / brand name for station cards.
 * Prefers affiliate BRAND label, then known provider slugs from `network` or `stationCode`.
 */
export function getFuelNetworkDisplayName(station: {
  network?: string | null;
  stationCode?: string | null;
  stationName?: string | null;
  affiliates?: Array<{ afType?: string | null; afName?: string | null; afCode?: string | null }> | null;
}): string | null {
  const slug = resolveNetworkSlug(station);
  if (slug === 'ambest') return 'AMBEST';
  if (slug && FUEL_NETWORK_LABELS[slug]) return FUEL_NETWORK_LABELS[slug];

  const brandAffiliate = (station.affiliates ?? []).find(
    (a) => String(a?.afType || '').toUpperCase() === 'BRAND'
  );
  const afName = (brandAffiliate?.afName ?? '').trim();
  if (afName) return afName;

  return null;
}
